/**
 * GOAFREET MASTER SERVICES TAXONOMY & MEGA MENU CONFIGURATION
 * Canonical source of truth for Services Mega Menu, Mobile Drawer, Footer, and Services Hub.
 * 
 * 10 Canonical Service Families:
 * 1. Brand, Creative & Experience
 * 2. Digital Marketing & Growth
 * 3. Ecommerce & Marketplace
 * 4. AI, Automation & Data
 * 5. Software Engineering
 * 6. Cloud, DevOps & Cybersecurity
 * 7. Integrations & Enterprise Platforms
 * 8. Emerging Technology & IoT
 * 9. Production, PR & Offline Media
 * 10. Consulting, Research & Transformation
 */

export const serviceTerminology = {
  main: "Services",
  categories: "Service Areas",
  item: "Services",
  solutions: "Solutions",
  technologies: "Technologies",
  industries: "Industries",
  portfolio: "Service Portfolio",
  explore: "Explore Services",
  viewAll: "View All Services",
  overview: "Service Overview",
};

export const SERVICE_FAMILIES = [
  {
    id: "brand-creative",
    slug: "brand-creative",
    railLabel: "Brand & Creative",
    fullTitle: "Brand, Creative & Experience",
    icon: "🎨",
    badgeColor: "#E91E63",
    overviewRoute: "/services/brand-creative/",
    shortOverview: "Brand strategy, visual identity systems, digital UI/UX design, and creative product execution.",
    description: "End-to-end brand positioning, multi-touchpoint design systems, digital product experiences, and high-conversion packaging that command market authority.",
    capabilities: [
      {
        label: "Brand Strategy & Identity",
        route: "/services/brand-creative/branding/",
        description: "Positioning, core messaging, brand architecture & naming.",
        featured: true,
      },
      {
        label: "Logo Design & Visual Systems",
        route: "/services/brand-creative/logo-designing/",
        description: "Distinctive logo marks, typography & brand guidelines.",
        featured: true,
      },
      {
        label: "UI/UX & Product Design",
        route: "/services/brand-creative/ui-ux-designing/",
        description: "Web, mobile, SaaS dashboards & conversion-focused journeys.",
        featured: true,
      },
      {
        label: "Packaging Design & Artwork",
        route: "/services/brand-creative/packaging-design/",
        description: "Retail packaging, technical dielines & 3D mockups.",
        featured: true,
      },
      {
        label: "Graphic Design & Creatives",
        route: "/services/brand-creative/graphics-designing/",
        description: "Multi-channel advertising graphics, banners & infographics.",
        featured: false,
      },
      {
        label: "Design Systems & Prototyping",
        route: "/services/brand-creative/design-systems-prototyping/",
        description: "Scalable component libraries, wireframes & user flows.",
        featured: false,
      },
      {
        label: "Marketing Collateral & Decks",
        route: "/services/brand-creative/marketing-collateral-design/",
        description: "Investor pitch decks, brochures & company profiles.",
        featured: false,
      },
      {
        label: "Digital Product Experience",
        route: "/services/brand-creative/digital-product-experience/",
        description: "Enterprise UX, interaction design & customer experience.",
        featured: false,
      },
    ],
  },
  {
    id: "digital-marketing",
    slug: "digital-marketing",
    railLabel: "Digital Marketing",
    fullTitle: "Digital Marketing & Growth",
    icon: "📈",
    badgeColor: "#10B981",
    overviewRoute: "/services/digital-marketing/",
    shortOverview: "Search engine dominance, paid acquisition, social brand acceleration, and full-funnel lead generation.",
    description: "Data-led organic and paid marketing frameworks driving qualified customer acquisition, sustainable CAC reduction, and measurable brand reach across India and global markets.",
    capabilities: [
      {
        label: "Search Engine Optimization (SEO)",
        route: "/services/digital-marketing/search-engine-optimization/",
        description: "Technical SEO, Core Web Vitals, entity search & ranking dominance.",
        featured: true,
      },
      {
        label: "Performance Marketing & PPC",
        route: "/services/digital-marketing/pay-per-click/",
        description: "Google Search, Display, Shopping & Performance Max campaigns.",
        featured: true,
      },
      {
        label: "Social Media Marketing",
        route: "/services/digital-marketing/social-media-marketing/",
        description: "Multi-platform organic social strategy & community growth.",
        featured: true,
      },
      {
        label: "Instagram & Meta Advertising",
        route: "/services/digital-marketing/instagram-marketing/",
        description: "High-converting Reels, feed creatives & Meta ad funnels.",
        featured: true,
      },
      {
        label: "B2B Lead Generation",
        route: "/services/digital-marketing/lead-generation/",
        description: "Targeted outbound prospect pipelines & sales qualification.",
        featured: true,
      },
      {
        label: "LinkedIn B2B Marketing",
        route: "/services/digital-marketing/linkedIn-marketing/",
        description: "Executive thought leadership & decision-maker outreach.",
        featured: false,
      },
      {
        label: "Facebook Marketing",
        route: "/services/digital-marketing/facebook-marketing/",
        description: "Full-funnel Meta acquisition, retargeting & audience scaling.",
        featured: false,
      },
      {
        label: "YouTube Video Marketing",
        route: "/services/digital-marketing/youtube-marketing/",
        description: "Video SEO, audience retention & brand channel management.",
        featured: false,
      },
      {
        label: "Twitter / X Marketing",
        route: "/services/digital-marketing/twitter-marketing/",
        description: "Real-time trends, PR amplification & active brand dialogue.",
        featured: false,
      },
      {
        label: "Content Marketing & Copywriting",
        route: "/services/digital-marketing/content-marketing/",
        description: "Topic clusters, authority pillar content & landing page copy.",
        featured: false,
      },
    ],
  },
  {
    id: "ecommerce-marketplace",
    slug: "ecommerce-marketplace",
    railLabel: "Ecommerce",
    fullTitle: "Ecommerce & Marketplace",
    icon: "🛒",
    badgeColor: "#F97316",
    overviewRoute: "/services/ecommerce-marketplace/",
    shortOverview: "Marketplace operations, seller acceleration, ecommerce engineering, and retail-channel dominance.",
    description: "End-to-end seller management across Amazon, Flipkart, and Meesho combined with custom D2C storefront engineering to maximize Buy Box share and revenue velocity.",
    capabilities: [
      {
        label: "Amazon Account Management",
        route: "/services/ecommerce-marketplace/amazon-account-management-services/",
        description: "Full-cycle seller operations, A+ content, Brand Store & FBA.",
        featured: true,
      },
      {
        label: "Flipkart Account Management",
        route: "/services/ecommerce-marketplace/flipkart-account-management-services/",
        description: "Listing optimization, catalog health & seller tier progression.",
        featured: true,
      },
      {
        label: "Meesho Account Management",
        route: "/services/ecommerce-marketplace/meesho-account-management-services/",
        description: "Catalog scaling, price parity & tier-2/3 volume growth.",
        featured: true,
      },
      {
        label: "Ecommerce Development",
        route: "/services/ecommerce-marketplace/ecommerce-development/",
        description: "Shopify, WooCommerce, custom headless & high-speed stores.",
        featured: true,
      },
      {
        label: "Marketplace Advertising (PPC)",
        route: "/services/ecommerce-marketplace/marketplace-advertising/",
        description: "Amazon Sponsored Ads, Flipkart PLA/PCA & ROAS scaling.",
        featured: true,
      },
      {
        label: "Meesho Ecommerce Marketing",
        route: "/services/ecommerce-marketplace/mesho-ecommerce-marketing/",
        description: "Promotional campaigns, deals & order velocity management.",
        featured: false,
      },
      {
        label: "Quick Commerce Catalog Operations",
        route: "/services/ecommerce-marketplace/quick-commerce-catalog-operations/",
        description: "Blinkit, Zepto & Instamart cataloging & inventory planning.",
        featured: false,
      },
      {
        label: "Headless Commerce & B2B Portals",
        route: "/services/ecommerce-marketplace/headless-commerce-b2b-portals/",
        description: "Custom checkout pipelines, wholesale portals & integrations.",
        featured: false,
      },
    ],
  },
  {
    id: "ai-automation",
    slug: "ai-automation",
    railLabel: "AI & Automation",
    fullTitle: "AI, Automation & Data",
    icon: "🤖",
    badgeColor: "#8B5CF6",
    overviewRoute: "/services/ai-automation/",
    shortOverview: "AI-enabled applications, intelligent workflow automation, data pipelines, and business transformation.",
    description: "Engineering pragmatic artificial intelligence solutions — including AI-assisted development, multi-agent workflows, conversational bots, computer vision, and CRM automation.",
    capabilities: [
      {
        label: "Vibe Coding & AI-Assisted Dev",
        route: "/services/ai-automation/vibe-coding-ai-assisted-development/",
        description: "Rapid, AI-accelerated product prototyping and software development.",
        featured: true,
      },
      {
        label: "AI Agents & Workflow Systems",
        route: "/services/ai-automation/ai-agents-workflow-automation/",
        description: "Autonomous task execution, multi-agent pipelines & triggers.",
        featured: true,
      },
      {
        label: "Conversational AI & Bots",
        route: "/services/ai-automation/conversational-ai-chatbots/",
        description: "Contextual customer support bots, assistants & lead capture.",
        featured: true,
      },
      {
        label: "CRM & Marketing Automation",
        route: "/services/ai-automation/crm-and-marketing-automation/",
        description: "Lead scoring, WhatsApp Business API & automated drip sequences.",
        featured: true,
      },
      {
        label: "Computer Vision & Image Models",
        route: "/services/ai-automation/computer-vision/",
        description: "Visual inspection, object classification & predictive vision.",
        featured: false,
      },
      {
        label: "Data Engineering & BI Dashboards",
        route: "/services/ai-automation/data-engineering-bi/",
        description: "ETL pipelines, data warehousing & executive analytics dashboards.",
        featured: false,
      },
      {
        label: "RPA & Business Process Automation",
        route: "/services/ai-automation/rpa-business-process-automation/",
        description: "UiPath, n8n & automated cross-system workflow orchestration.",
        featured: false,
      },
      {
        label: "Custom AI IDE & Developer Tools",
        route: "/services/ai-automation/custom-ai-developer-tools/",
        description: "Tailored AI-powered coding utilities and internal development aids.",
        featured: false,
      },
      {
        label: "AI Custom ERP Systems",
        route: "/services/ai-automation/ai-custom-erp/",
        description: "Intelligent enterprise resource planning & automated forecasting.",
        featured: false,
      },
      {
        label: "AI-Assisted Legacy Modernization",
        route: "/services/ai-automation/ai-legacy-modernization/",
        description: "Automated code refactoring, schema migrations & cloud readiness.",
        featured: false,
      },
    ],
  },
  {
    id: "software-engineering",
    slug: "software-engineering",
    railLabel: "Software Engineering",
    fullTitle: "Software Engineering",
    icon: "💻",
    badgeColor: "#2196F3",
    overviewRoute: "/services/software-engineering/",
    shortOverview: "Web, mobile, desktop, custom ERP, and enterprise software engineering from architecture to production.",
    description: "Robust, scalable software development across modern stacks (React, Gatsby, Next.js, Node.js, Python, Flutter) engineered for performance, security, and enterprise growth.",
    capabilities: [
      {
        label: "Website Development",
        route: "/services/software-engineering/website-development/",
        description: "Modern, high-speed corporate websites, web apps & portals.",
        featured: true,
      },
      {
        label: "Mobile App Development",
        route: "/services/software-engineering/mobile-app-development/",
        description: "Native iOS & Android engineering with Flutter & React Native.",
        featured: true,
      },
      {
        label: "Desktop Application Engineering",
        route: "/services/software-engineering/desktop-app-development/",
        description: "Cross-platform desktop tools & local business process apps.",
        featured: true,
      },
      {
        label: "Custom ERP & Business Systems",
        route: "/services/software-engineering/custom-erp-development/",
        description: "Operations, finance, inventory & supply chain workflows.",
        featured: true,
      },
      {
        label: "Website Maintenance & Support",
        route: "/services/software-engineering/website-maintenance/",
        description: "Core Web Vitals, security patches & SLA-backed support.",
        featured: true,
      },
      {
        label: "Full-Stack Web Applications",
        route: "/services/software-engineering/full-stack-web-application-development/",
        description: "SaaS platforms, admin dashboards & client collaboration portals.",
        featured: false,
      },
      {
        label: "QA Testing & Test Automation",
        route: "/services/software-engineering/qa-testing-automation/",
        description: "End-to-end, API, UI, performance & regression testing suites.",
        featured: false,
      },
      {
        label: "Unity 3D & Interactive Visuals",
        route: "/services/software-engineering/unity-3d-development/",
        description: "Interactive 3D simulations & real-time product visualizations.",
        featured: false,
      },
      {
        label: "Low-Code & Power Apps",
        route: "/services/software-engineering/low-code-development/",
        description: "Rapid internal tools built on Microsoft Power Apps & workflow engines.",
        featured: false,
      },
    ],
  },
  {
    id: "cloud-devops",
    slug: "cloud-devops",
    railLabel: "Cloud & DevOps",
    fullTitle: "Cloud, DevOps & Cybersecurity",
    icon: "☁️",
    badgeColor: "#0284C7",
    overviewRoute: "/services/cloud-devops/",
    shortOverview: "Cloud infrastructure architecture, automated deployment pipelines, application modernization, and operational security.",
    description: "Production cloud deployments on AWS and Azure with containerized CI/CD pipelines, automated infrastructure, monitoring observability, and proactive application security hardening.",
    capabilities: [
      {
        label: "Cloud Architecture & Migration",
        route: "/services/cloud-devops/cloud-architecture-migration/",
        description: "Cloud-native architecture, compute setup & workload migrations.",
        featured: true,
      },
      {
        label: "AWS Cloud Infrastructure",
        route: "/services/cloud-devops/aws-cloud-infrastructure/",
        description: "EC2, S3, RDS, Lambda serverless & VPC network engineering.",
        featured: true,
      },
      {
        label: "Azure Cloud Engineering",
        route: "/services/cloud-devops/azure-cloud-engineering/",
        description: "Enterprise Azure app services, SQL instances & cloud storage.",
        featured: true,
      },
      {
        label: "DevOps & CI/CD Pipelines",
        route: "/services/cloud-devops/devops-ci-cd/",
        description: "Automated GitHub Actions, Docker containers & release pipelines.",
        featured: true,
      },
      {
        label: "Application Security Hardening",
        route: "/services/cloud-devops/application-security-hardening/",
        description: "Vulnerability reviews, IAM access control & secure coding.",
        featured: true,
      },
      {
        label: "Managed Operations & Maintenance",
        route: "/services/cloud-devops/managed-application-operations/",
        description: "Monitoring, uptime management, log tracking & SLA support.",
        featured: false,
      },
      {
        label: "Application Modernization",
        route: "/services/cloud-devops/application-modernization/",
        description: "Legacy code refactoring, replatforming & containerization.",
        featured: false,
      },
      {
        label: "Observability & Performance Tuning",
        route: "/services/cloud-devops/observability-performance/",
        description: "APM tracing, server metrics, database indexing & load testing.",
        featured: false,
      },
    ],
  },
  {
    id: "integrations-enterprise",
    slug: "integrations-enterprise",
    railLabel: "Integrations",
    fullTitle: "Integrations & Enterprise Platforms",
    icon: "🔌",
    badgeColor: "#6366F1",
    overviewRoute: "/services/integrations-enterprise/",
    shortOverview: "Third-party API connectors, enterprise plugins, CRM integrations, and automated business synchronizations.",
    description: "Seamlessly connecting disparate software systems through bespoke API integrations, browser extensions, Adobe and Microsoft plugins, Salesforce connectors, and Web-to-Print portals.",
    capabilities: [
      {
        label: "API & System Integrations",
        route: "/services/integrations-enterprise/api-system-integrations/",
        description: "REST & GraphQL APIs, webhooks, iPaaS & automated synchronization.",
        featured: true,
      },
      {
        label: "Browser Extensions",
        route: "/services/integrations-enterprise/browser-extension-development/",
        description: "Custom Chrome, Edge & Firefox productivity and automation plugins.",
        featured: true,
      },
      {
        label: "Adobe Creative Cloud Plugins",
        route: "/services/integrations-enterprise/adobe-plugin-development/",
        description: "Photoshop & Illustrator workflow automation extensions.",
        featured: true,
      },
      {
        label: "Microsoft 365 & Office Add-ins",
        route: "/services/integrations-enterprise/microsoft-365-addin-development/",
        description: "Word, Excel, Outlook & Teams business application add-ins.",
        featured: true,
      },
      {
        label: "Salesforce CRM Integrations",
        route: "/services/integrations-enterprise/salesforce-integrations/",
        description: "Apex, Lightning components, lead routing & CRM connectors.",
        featured: true,
      },
      {
        label: "Web-to-Print (W2P) Portals",
        route: "/services/integrations-enterprise/web-to-print/",
        description: "Online print configuration, artwork automation & ordering systems.",
        featured: false,
      },
      {
        label: "ERP & Financial Connectors",
        route: "/services/integrations-enterprise/erp-financial-integrations/",
        description: "Accounting SaaS, inventory sync & multi-channel order feeds.",
        featured: false,
      },
      {
        label: "Open Source Enterprise Solutions",
        route: "/services/integrations-enterprise/open-source-enterprise-solutions/",
        description: "Mautic, custom CRM setups & self-hosted business platforms.",
        featured: false,
      },
    ],
  },
  {
    id: "emerging-tech-iot",
    slug: "emerging-tech-iot",
    railLabel: "Emerging Tech & IoT",
    fullTitle: "Emerging Technology & IoT",
    icon: "🌐",
    badgeColor: "#059669",
    overviewRoute: "/services/emerging-tech-iot/",
    shortOverview: "Connected IoT devices, immersive AR/VR experiences, wearable apps, and next-generation digital interfaces.",
    description: "Bridging the physical and digital worlds with IoT telemetry platforms, interactive WebXR and augmented reality, Smart TV apps, wearable software, and telematics UX.",
    capabilities: [
      {
        label: "IoT Solutions & Telemetry",
        route: "/services/emerging-tech-iot/iot-solutions/",
        description: "Connected device monitoring, real-time sensor dashboards & MQTT.",
        featured: true,
      },
      {
        label: "AR / VR & Immersive WebXR",
        route: "/services/emerging-tech-iot/ar-vr-webxr/",
        description: "Interactive 3D product previews, WebXR & virtual walkthroughs.",
        featured: true,
      },
      {
        label: "Wearable Device Applications",
        route: "/services/emerging-tech-iot/wearable-app-development/",
        description: "Companion apps for Apple Watch, Wear OS & fitness trackers.",
        featured: true,
      },
      {
        label: "Smart TV & OTT Applications",
        route: "/services/emerging-tech-iot/smart-tv-ott-app-development/",
        description: "Streaming apps for Apple TV, Fire TV, Android TV & Tizen.",
        featured: true,
      },
      {
        label: "Blockchain Workflow Integration",
        route: "/services/emerging-tech-iot/blockchain-integration/",
        description: "Verifiable smart contract triggers & decentralized asset records.",
        featured: false,
      },
      {
        label: "Automotive IoT & Telematics UX",
        route: "/services/emerging-tech-iot/automotive-iot-telematics/",
        description: "Fleet tracking dashboards, in-car displays & telematics interfaces.",
        featured: false,
      },
    ],
  },
  {
    id: "production-offline",
    slug: "production-offline",
    railLabel: "Production & Offline",
    fullTitle: "Production, PR & Offline Media",
    icon: "🎬",
    badgeColor: "#DC2626",
    overviewRoute: "/services/production-offline/",
    shortOverview: "Commercial film production, creator campaigns, transit advertising, commercial print, and brand protection.",
    description: "High-impact visual production and physical brand presence across corporate brand films, commercial TVC/DVCs, auto rickshaw transit media, influencer outreach, and Online Reputation Management.",
    capabilities: [
      {
        label: "Corporate Video & Films",
        route: "/services/production-offline/video-production/",
        description: "Cinematic company stories, leadership interviews & brand films.",
        featured: true,
      },
      {
        label: "Commercial & DVC Production",
        route: "/services/production-offline/commercial-dvc-production/",
        description: "High-impact digital ad films, social shorts & product spots.",
        featured: true,
      },
      {
        label: "Online Reputation Management (ORM)",
        route: "/services/production-offline/online-reputation-management/",
        description: "Review defense, brand sentiment recovery & search presence.",
        featured: true,
      },
      {
        label: "Transit Advertising (Auto Rickshaw)",
        route: "/services/production-offline/auto-riksha-banner-marketing/",
        description: "High-density mobile outdoor transit hoods & banner advertising.",
        featured: true,
      },
      {
        label: "Influencer & Creator Marketing",
        route: "/services/production-offline/influencer-marketing/",
        description: "Creator vetting, campaign seeding & performance collaborations.",
        featured: true,
      },
      {
        label: "Packaging Design & Print Setup",
        route: "/services/production-offline/packaging-print-production/",
        description: "Print-ready packaging dielines, materials & color consistency.",
        featured: false,
      },
      {
        label: "Commercial Photography & 360°",
        route: "/services/production-offline/commercial-photography/",
        description: "Studio product photography, catalog shots & 360° spin imagery.",
        featured: false,
      },
      /*
      {
        label: "Exhibition & Event Branding",
        route: "/services/production-offline/exhibition-event-branding/",
        description: "Trade show booth design, display panels & event collateral.",
        featured: false,
        upcoming: true,
      },
      */
      {
        label: "Motion Graphics & 3D Animation",
        route: "/services/production-offline/motion-graphics-3d-animation/",
        description: "Explainer videos, 3D product animation & visual effects.",
        featured: false,
      },
    ],
  },
  {
    id: "consulting-transformation",
    slug: "consulting-transformation",
    railLabel: "Consulting",
    fullTitle: "Consulting, Research & Transformation",
    icon: "🧭",
    badgeColor: "#4F46E5",
    overviewRoute: "/services/consulting-transformation/",
    shortOverview: "Strategic advisory, digital transformation, market research, and technology roadmaps for sustainable growth.",
    description: "Executive advisory guiding business leadership through market positioning, competitor research, AI adoption, technology architecture, and new retail market entry.",
    capabilities: [
      {
        label: "Digital Strategy & Transformation",
        route: "/services/consulting-transformation/digital-strategy-transformation/",
        description: "End-to-end digital roadmap, operating models & technology stack.",
        featured: true,
      },
      {
        label: "Market & Competitor Research",
        route: "/services/consulting-transformation/market-competitor-research/",
        description: "Market opportunity sizing, competitor audits & consumer intelligence.",
        featured: true,
      },
      {
        label: "Brand Positioning & Audits",
        route: "/services/consulting-transformation/brand-positioning-audit/",
        description: "Brand equity assessments, naming strategy & perception analysis.",
        featured: true,
      },
      {
        label: "Go-To-Market & Growth Strategy",
        route: "/services/consulting-transformation/go-to-market-growth-strategy/",
        description: "Multi-channel launch strategies, pricing & customer acquisition.",
        featured: true,
      },
      {
        label: "Technology Architecture Advisory",
        route: "/services/consulting-transformation/technology-architecture-advisory/",
        description: "Tech stack evaluation, scalability audits & platform selection.",
        featured: true,
      },
      {
        label: "AI Strategy & Process Roadmaps",
        route: "/services/consulting-transformation/ai-strategy-roadmap/",
        description: "Identifying high-ROI AI automation & workflow transformation.",
        featured: false,
      },
      {
        label: "Ecommerce & Retail Channel Advisory",
        route: "/services/consulting-transformation/ecommerce-retail-advisory/",
        description: "Channel profitability, catalog architecture & marketplace scale.",
        featured: false,
      },
    ],
  },
];

/**
 * Route migration matrix ensuring 100% route preservation.
 */
export const ALL_EXISTING_SERVICES_INVENTORY = [
  { path: "/services/brand-creative/branding/", name: "Brand Strategy & Identity", family: "brand-creative" },
  { path: "/services/brand-creative/logo-designing/", name: "Logo Design & Visual Assets", family: "brand-creative" },
  { path: "/services/brand-creative/graphics-designing/", name: "Graphics & Creative Design", family: "brand-creative" },
  { path: "/services/brand-creative/ui-ux-designing/", name: "UI/UX & Product Design", family: "brand-creative" },
  { path: "/services/brand-creative/packaging-design/", name: "Product Packaging Design", family: "brand-creative" },
  { path: "/services/digital-marketing/search-engine-optimization/", name: "Search Engine Optimization (SEO)", family: "digital-marketing" },
  { path: "/services/digital-marketing/pay-per-click/", name: "Pay Per Click (PPC) Management", family: "digital-marketing" },
  { path: "/services/digital-marketing/social-media-marketing/", name: "Social Media Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/instagram-marketing/", name: "Instagram Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/facebook-marketing/", name: "Facebook Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/linkedIn-marketing/", name: "LinkedIn B2B Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/youtube-marketing/", name: "YouTube Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/twitter-marketing/", name: "Twitter / X Marketing", family: "digital-marketing" },
  { path: "/services/digital-marketing/lead-generation/", name: "B2B Lead Generation", family: "digital-marketing" },
  { path: "/services/ecommerce-marketplace/amazon-account-management-services/", name: "Amazon Account Management", family: "ecommerce-marketplace" },
  { path: "/services/ecommerce-marketplace/flipkart-account-management-services/", name: "Flipkart Account Management", family: "ecommerce-marketplace" },
  { path: "/services/ecommerce-marketplace/flipkart-marketing/", name: "Flipkart Marketing & Ads", family: "ecommerce-marketplace" },
  { path: "/services/ecommerce-marketplace/meesho-account-management-services/", name: "Meesho Account Management", family: "ecommerce-marketplace" },
  { path: "/services/ecommerce-marketplace/mesho-ecommerce-marketing/", name: "Meesho Ecommerce Growth", family: "ecommerce-marketplace" },
  { path: "/services/ecommerce-marketplace/ecommerce-development/", name: "Ecommerce Development", family: "ecommerce-marketplace" },
  { path: "/services/ai-automation/crm-and-marketing-automation/", name: "CRM & Marketing Automation", family: "ai-automation" },
  { path: "/services/software-engineering/website-development/", name: "Website Development", family: "software-engineering" },
  { path: "/services/software-engineering/mobile-app-development/", name: "Mobile App Development", family: "software-engineering" },
  { path: "/services/software-engineering/desktop-app-development/", name: "Desktop Application Engineering", family: "software-engineering" },
  { path: "/services/software-engineering/website-maintenance/", name: "Website Maintenance & Support", family: "software-engineering" },
  { path: "/services/production-offline/video-production/", name: "Corporate & Brand Films", family: "production-offline" },
  { path: "/services/production-offline/influencer-marketing/", name: "Influencer & Creator Marketing", family: "production-offline" },
  { path: "/services/production-offline/online-reputation-management/", name: "Online Reputation Management", family: "production-offline" },
  { path: "/services/production-offline/auto-riksha-banner-marketing/", name: "Transit Advertising (Auto Rickshaw)", family: "production-offline" },
];

/**
 * Footer service columns (Section 41)
 */
export const FOOTER_SERVICE_GROUPS = {
  creative: {
    title: "Brand & Creative",
    links: [
      { label: "Brand Strategy & Identity", to: "/services/brand-creative/branding/" },
      { label: "Logo Design & Visual Assets", to: "/services/brand-creative/logo-designing/" },
      { label: "UI/UX & Product Design", to: "/services/brand-creative/ui-ux-designing/" },
      { label: "Packaging Design", to: "/services/brand-creative/packaging-design/" },
      { label: "Graphic Design", to: "/services/brand-creative/graphics-designing/" },
      { label: "Explore Brand Services", to: "/services/brand-creative/" },
    ],
  },
  marketing: {
    title: "Digital Marketing",
    links: [
      { label: "Search Engine Optimization", to: "/services/digital-marketing/search-engine-optimization/" },
      { label: "Performance Marketing (PPC)", to: "/services/digital-marketing/pay-per-click/" },
      { label: "Social Media Marketing", to: "/services/digital-marketing/social-media-marketing/" },
      { label: "Instagram & Meta Ads", to: "/services/digital-marketing/instagram-marketing/" },
      { label: "B2B Lead Generation", to: "/services/digital-marketing/lead-generation/" },
      { label: "Explore Digital Marketing", to: "/services/digital-marketing/" },
    ],
  },
  ecommerce: {
    title: "Ecommerce",
    links: [
      { label: "Amazon Account Management", to: "/services/ecommerce-marketplace/amazon-account-management-services/" },
      { label: "Flipkart Account Management", to: "/services/ecommerce-marketplace/flipkart-account-management-services/" },
      { label: "Meesho Account Management", to: "/services/ecommerce-marketplace/meesho-account-management-services/" },
      { label: "Ecommerce Development", to: "/services/ecommerce-marketplace/ecommerce-development/" },
      { label: "Marketplace Advertising", to: "/services/ecommerce-marketplace/flipkart-marketing/" },
      { label: "Explore Ecommerce", to: "/services/ecommerce-marketplace/" },
    ],
  },
  technology: {
    title: "Software Engineering",
    links: [
      { label: "Website Development", to: "/services/software-engineering/website-development/" },
      { label: "Mobile App Development", to: "/services/software-engineering/mobile-app-development/" },
      { label: "Desktop Applications", to: "/services/software-engineering/desktop-app-development/" },
      { label: "Custom ERP Systems", to: "/services/software-engineering/desktop-app-development/" },
      { label: "Website Maintenance & Support", to: "/services/software-engineering/website-maintenance/" },
      { label: "Explore Software Engineering", to: "/services/software-engineering/" },
    ],
  },
  automation: {
    title: "AI & Automation",
    links: [
      { label: "Vibe Coding & AI Dev", to: "/services/software-engineering/website-development/" },
      { label: "AI Agents & Automation", to: "/services/ai-automation/crm-and-marketing-automation/" },
      { label: "Conversational AI Bots", to: "/services/ai-automation/crm-and-marketing-automation/" },
      { label: "CRM & Drip Automation", to: "/services/ai-automation/crm-and-marketing-automation/" },
      { label: "Cloud & DevOps", to: "/services/cloud-devops/" },
      { label: "Explore AI & Automation", to: "/services/ai-automation/" },
    ],
  },
  production: {
    title: "Production & Media",
    links: [
      { label: "Corporate Video & Films", to: "/services/production-offline/video-production/" },
      { label: "Online Reputation (ORM)", to: "/services/production-offline/online-reputation-management/" },
      { label: "Transit Advertising (Auto Rickshaw)", to: "/services/production-offline/auto-riksha-banner-marketing/" },
      { label: "Influencer Marketing", to: "/services/production-offline/influencer-marketing/" },
      { label: "Consulting & Strategy", to: "/services/consulting-transformation/" },
      { label: "View All Services", to: "/services/" },
    ],
  },
};

// Ensure both .services and .capabilities exist for all families
SERVICE_FAMILIES.forEach((family) => {
  if (!family.services) {
    family.services = family.capabilities;
  }
});
